import React from 'react';

import PropTypes from 'prop-types';

/**
 * RenderIf is a React component that conditionally renders its children
 * based on a boolean condition. If the condition (`when`) is true,
 * it renders the `children`. Otherwise, it renders a fallback, which
 * can be `null` or any other valid React node.
 *
 * @component
 * @example
 * // Basic usage:
 * <RenderIf when={true}>
 *   <div>Content to render if condition is true</div>
 * </RenderIf>
 *
 * @example
 * // Usage with a fallback:
 * <RenderIf when={false} fallback={<div>No content available</div>}>
 *   <div>Content to render if condition is true</div>
 * </RenderIf>
 *
 * @param {object} props - The properties passed to the component.
 * @param {React.ReactNode} props.children - The content to render if the condition is true.
 * @param {boolean} props.when - The condition that determines whether to render the `children` or the `fallback`.
 * @param {React.ReactNode} [props.fallback=null] - The content to render if the condition is false. Defaults to `null`.
 *
 * @returns {React.ReactNode} - Returns `children` if `when` is true, otherwise returns `fallback`.
 */
export default function RenderIf({ children, when, fallback = null }) {
  return when ? children : fallback;
}

RenderIf.propTypes = {
  /**
   * The condition that determines whether to render the `children` or the `fallback`.
   */
  when: PropTypes.bool.isRequired,

  /**
   * The content to render if the condition (`when`) is true.
   */
  children: PropTypes.node.isRequired,

  /**
   * The content to render if the condition (`when`) is false.
   * It can be `null` or any valid React node.
   */
  fallback: PropTypes.oneOfType([PropTypes.node, PropTypes.oneOf([null])])
};
